import { useQuery } from '@tanstack/react-query'
import { AlertCircle, ArrowLeft, GitCompareArrows, Users } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import { GateFailures } from '../components/GateFailures'
import { TierBadge } from '../components/TierBadge'
import type { AgentListItem, AgentSummary, Tier } from '../types'

const METRIC_ORDER = [
  'talk_ratio',
  'longest_monologue',
  'next_step_commitment',
  'objection_delta',
  'dispo_alignment',
] as const

const METRIC_LABELS: Record<string, string> = {
  talk_ratio: 'Talk ratio',
  longest_monologue: 'Monologue',
  next_step_commitment: 'Next-step',
  objection_delta: 'Objection',
  dispo_alignment: 'Dispo',
}

function formatPct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '--'
  return `${(rate * 100).toFixed(2)}%`
}

// Pull tier/rates from either summary top-level or nested agent (backend rollout).
function pickTierFields(data: AgentSummary) {
  return {
    tier: (data.tier ?? data.agent.tier ?? null) as Tier | null,
    tier_name: data.tier_name ?? data.agent.tier_name ?? null,
    per_dial_rate: data.per_dial_rate ?? data.agent.per_dial_rate ?? null,
    per_contact_rate: data.per_contact_rate ?? data.agent.per_contact_rate ?? null,
    dnc_rate: data.dnc_rate ?? data.agent.dnc_rate ?? null,
    gate_failures: data.gate_failures ?? null,
  }
}

export function AgentComparePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const a = searchParams.get('a') ?? ''
  const b = searchParams.get('b') ?? ''

  const needsPicker = !a || !b

  // Always fetch agent list (cheap; reused for picker + dropdown options).
  const { data: agentList, error: listError } = useQuery({
    queryKey: ['agents', 'list'],
    queryFn: () => api.agents.list(),
  })

  const summaryA = useQuery({
    queryKey: ['agent-summary', a],
    queryFn: () => api.agents.summary(a),
    enabled: Boolean(a),
  })

  const summaryB = useQuery({
    queryKey: ['agent-summary', b],
    queryFn: () => api.agents.summary(b),
    enabled: Boolean(b),
  })

  if (needsPicker) {
    return (
      <AgentPicker
        agents={agentList ?? []}
        listError={listError as Error | null}
        initialA={a}
        initialB={b}
        onSubmit={(nextA, nextB) => {
          const next = new URLSearchParams()
          next.set('a', nextA)
          next.set('b', nextB)
          setSearchParams(next)
        }}
      />
    )
  }

  const errorA = summaryA.error as Error | undefined
  const errorB = summaryB.error as Error | undefined
  const loading = summaryA.isLoading || summaryB.isLoading
  const dataA = summaryA.data
  const dataB = summaryB.data

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-2">
        <GitCompareArrows className="h-5 w-5 text-indigo-400" />
        <h1 className="text-xl font-semibold">Agent comparison</h1>
        <span className="font-mono text-sm text-slate-400">
          {a} <span className="text-slate-600">vs</span> {b}
        </span>
        <Link
          to="/agents/compare"
          className="ml-auto rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800"
        >
          Change agents
        </Link>
      </header>

      {(errorA || errorB) && (
        <div className="space-y-2">
          {errorA && <ErrorBanner side="left" agent={a} error={errorA} />}
          {errorB && <ErrorBanner side="right" agent={b} error={errorB} />}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-80 animate-pulse rounded bg-slate-800/60" />
          <div className="h-80 animate-pulse rounded bg-slate-800/60" />
        </div>
      ) : !dataA || !dataB ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-8 text-center text-sm text-slate-400">
          One or both agents could not be loaded.{' '}
          <Link to="/agents/compare" className="text-indigo-300 underline-offset-2 hover:underline">
            Pick again
          </Link>
          .
        </div>
      ) : (
        <ComparisonBody dataA={dataA} dataB={dataB} />
      )}
    </div>
  )
}

function ErrorBanner({
  side,
  agent,
  error,
}: {
  side: 'left' | 'right'
  agent: string
  error: Error
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
      <AlertCircle className="mt-0.5 h-4 w-4" />
      <div className="min-w-0">
        <p className="font-medium">
          {side === 'left' ? 'Left' : 'Right'} agent{' '}
          <span className="font-mono">{agent}</span> failed to load
        </p>
        <p className="text-xs text-red-300/80">{error.message}</p>
      </div>
    </div>
  )
}

function ComparisonBody({ dataA, dataB }: { dataA: AgentSummary; dataB: AgentSummary }) {
  const fieldsA = pickTierFields(dataA)
  const fieldsB = pickTierFields(dataB)

  return (
    <>
      {/* Agent headers + 4-tile stat grids, side-by-side. */}
      <section className="grid gap-4 md:grid-cols-2">
        <AgentColumnHeader data={dataA} fields={fieldsA} />
        <AgentColumnHeader data={dataB} fields={fieldsB} />
      </section>

      {/* Per-metric pass-rate bar comparison with delta highlight. */}
      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-200">
          Per-metric pass rate (side-by-side)
        </h2>
        <div className="space-y-3">
          {METRIC_ORDER.map((m) => (
            <MetricCompareRow
              key={m}
              metric={m}
              label={METRIC_LABELS[m]}
              rateA={(dataA.per_metric_pass_rate as Record<string, number>)[m]}
              rateB={(dataB.per_metric_pass_rate as Record<string, number>)[m]}
              nameA={dataA.agent.full_name ?? dataA.agent.vici_user}
              nameB={dataB.agent.full_name ?? dataB.agent.vici_user}
            />
          ))}
        </div>
      </section>

      {/* Gate failures, listed for each. */}
      <section className="grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {dataA.agent.vici_user} blockers
          </h3>
          {fieldsA.gate_failures && fieldsA.gate_failures.length > 0 ? (
            <GateFailures failures={fieldsA.gate_failures} />
          ) : (
            <NoBlockers />
          )}
        </div>
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {dataB.agent.vici_user} blockers
          </h3>
          {fieldsB.gate_failures && fieldsB.gate_failures.length > 0 ? (
            <GateFailures failures={fieldsB.gate_failures} />
          ) : (
            <NoBlockers />
          )}
        </div>
      </section>
    </>
  )
}

function NoBlockers() {
  return (
    <div className="rounded-lg border border-emerald-900/40 bg-emerald-950/10 p-3 text-xs text-emerald-300">
      No promotion blockers. Tier 1 territory.
    </div>
  )
}

function AgentColumnHeader({
  data,
  fields,
}: {
  data: AgentSummary
  fields: ReturnType<typeof pickTierFields>
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Link
          to={`/agents/${encodeURIComponent(data.agent.vici_user)}`}
          className="font-semibold text-slate-100 hover:text-indigo-300"
        >
          {data.agent.vici_user}
        </Link>
        {data.agent.full_name && (
          <span className="text-sm text-slate-400">{data.agent.full_name}</span>
        )}
        {fields.tier !== null && <TierBadge tier={fields.tier} tierName={fields.tier_name} />}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Stat
          label="Avg overall"
          value={
            data.agent.avg_overall !== null && data.agent.avg_overall !== undefined
              ? data.agent.avg_overall.toFixed(1)
              : '--'
          }
          sub="out of 10.0"
        />
        <Stat label="Calls" value={data.counts.total} />
        <Stat label="Per-dial" value={formatPct(fields.per_dial_rate)} sub="bookings/dials" />
        <Stat
          label="Per-contact"
          value={formatPct(fields.per_contact_rate)}
          sub="bookings/contacts"
        />
        <Stat label="DNC rate" value={formatPct(fields.dnc_rate)} sub="DNC/dials" />
      </div>
    </div>
  )
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string
  value: string | number
  sub?: string
}) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/60 p-2.5">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 font-semibold text-slate-100 text-xl">{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-slate-500">{sub}</p>}
    </div>
  )
}

function MetricCompareRow({
  metric,
  label,
  rateA,
  rateB,
  nameA,
  nameB,
}: {
  metric: string
  label: string
  rateA: number | undefined
  rateB: number | undefined
  nameA: string
  nameB: string
}) {
  const hasA = typeof rateA === 'number'
  const hasB = typeof rateB === 'number'
  const aPct = hasA ? Math.min(100, Math.max(0, (rateA as number) * 100)) : 0
  const bPct = hasB ? Math.min(100, Math.max(0, (rateB as number) * 100)) : 0

  let leader: 'a' | 'b' | 'tie' = 'tie'
  let deltaText = '--'
  if (hasA && hasB) {
    const delta = aPct - bPct
    if (Math.abs(delta) < 0.5) {
      leader = 'tie'
      deltaText = 'tie'
    } else if (delta > 0) {
      leader = 'a'
      deltaText = `+${delta.toFixed(0)}pp`
    } else {
      leader = 'b'
      deltaText = `+${(-delta).toFixed(0)}pp`
    }
  }

  const tooltip =
    hasA && hasB
      ? `${nameA} ${label}: ${aPct.toFixed(0)}%, ${nameB} ${label}: ${bPct.toFixed(0)}%${
          leader === 'tie' ? ' — tie' : ` — delta ${deltaText} (${leader === 'a' ? nameA : nameB} leads)`
        }`
      : undefined

  return (
    <div data-metric={metric} title={tooltip} className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
      {/* LHS: agent A bar (right-aligned, fills toward center) */}
      <div className="flex items-center gap-2 justify-end">
        <span className="font-mono text-xs text-slate-300 w-10 text-right">
          {hasA ? `${aPct.toFixed(0)}%` : '--'}
        </span>
        <div
          className={`h-2.5 overflow-hidden rounded ${
            leader === 'a' ? 'ring-1 ring-emerald-500/60' : ''
          }`}
          style={{ width: '100%' }}
        >
          <div className="flex h-full justify-end bg-slate-800">
            <div
              className={leader === 'a' ? 'h-full bg-emerald-500' : 'h-full bg-slate-500'}
              style={{ width: `${aPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Center: metric label + delta */}
      <div className="flex flex-col items-center px-2 min-w-[7rem]">
        <span className="text-xs font-medium text-slate-200">{label}</span>
        <span
          className={`font-mono text-[10px] ${
            leader === 'tie'
              ? 'text-slate-500'
              : leader === 'a'
                ? 'text-emerald-300'
                : 'text-emerald-300'
          }`}
        >
          {deltaText}
        </span>
      </div>

      {/* RHS: agent B bar (left-aligned) */}
      <div className="flex items-center gap-2">
        <div
          className={`h-2.5 overflow-hidden rounded ${
            leader === 'b' ? 'ring-1 ring-emerald-500/60' : ''
          }`}
          style={{ width: '100%' }}
        >
          <div className="flex h-full bg-slate-800">
            <div
              className={leader === 'b' ? 'h-full bg-emerald-500' : 'h-full bg-slate-500'}
              style={{ width: `${bPct}%` }}
            />
          </div>
        </div>
        <span className="font-mono text-xs text-slate-300 w-10">
          {hasB ? `${bPct.toFixed(0)}%` : '--'}
        </span>
      </div>
    </div>
  )
}

// ---- Picker ----

function AgentPicker({
  agents,
  listError,
  initialA,
  initialB,
  onSubmit,
}: {
  agents: AgentListItem[]
  listError: Error | null
  initialA: string
  initialB: string
  onSubmit: (a: string, b: string) => void
}) {
  const sorted = useMemo(() => {
    return [...agents].sort((x, y) => {
      const xn = x.full_name ?? x.vici_user
      const yn = y.full_name ?? y.vici_user
      return xn.localeCompare(yn)
    })
  }, [agents])

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-2">
        <GitCompareArrows className="h-5 w-5 text-indigo-400" />
        <h1 className="text-xl font-semibold">Compare agents</h1>
        <Link
          to="/agents"
          className="ml-auto inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800"
        >
          <ArrowLeft className="h-3 w-3" />
          All agents
        </Link>
      </header>

      {listError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{listError.message}</span>
        </div>
      )}

      <p className="text-sm text-slate-400">
        Pick two agents to put side-by-side. Useful for coaching debriefs — same metrics,
        same window, hard delta numbers.
      </p>

      <form
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
        onSubmit={(e) => {
          e.preventDefault()
          const fd = new FormData(e.currentTarget)
          const a = String(fd.get('a') ?? '').trim()
          const b = String(fd.get('b') ?? '').trim()
          if (!a || !b) return
          onSubmit(a, b)
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <PickerColumn name="a" label="Agent A" agents={sorted} initial={initialA} />
          <PickerColumn name="b" label="Agent B" agents={sorted} initial={initialB} />
        </div>
        <button
          type="submit"
          className="mt-4 inline-flex items-center gap-1 rounded border border-indigo-700 bg-indigo-900/60 px-3 py-1.5 text-sm text-indigo-100 hover:bg-indigo-900"
        >
          <Users className="h-4 w-4" />
          Compare
        </button>
      </form>
    </div>
  )
}

function PickerColumn({
  name,
  label,
  agents,
  initial,
}: {
  name: 'a' | 'b'
  label: string
  agents: AgentListItem[]
  initial: string
}) {
  return (
    <div>
      <label
        htmlFor={`compare-${name}`}
        className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-400"
      >
        {label}
      </label>
      <select
        id={`compare-${name}`}
        name={name}
        defaultValue={initial}
        required
        className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
      >
        <option value="">— pick agent —</option>
        {agents.map((a) => (
          <option key={a.vici_user} value={a.vici_user}>
            {a.vici_user}
            {a.full_name ? ` — ${a.full_name}` : ''}
            {a.tier ? ` (T${a.tier})` : ''}
          </option>
        ))}
      </select>
    </div>
  )
}
